import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import pick from 'lodash-es/pick';
import { UAParser } from 'ua-parser-js';
import type { IncomingHttpHeaders } from 'node:http';
import type { IResult } from 'ua-parser-js';
import { BREAKPOINTS_PX } from '../../theme/foundations/breakpoints';
import { isServer } from '../../utils/utils';

export const clientHintsHeaders = [
    'user-agent',
    'sec-ch-ua-mobile',
    'sec-ch-viewport-width',
    'sec-ch-downlink',
    'sec-ch-ua-mobile',
] as const;

export type ClientHintHeaders = (typeof clientHintsHeaders)[number];

function isUAIResult(value: unknown): value is IResult {
    if (!isServer) {
        // CSR is Promise<IResult>
        return false;
    }
    // SSR is IResult (however they add a function called `then` which makes confusing)
    return (
        isServer &&
        !!value &&
        typeof value === 'object' &&
        'ua' in value &&
        'browser' in value &&
        'device' in value &&
        'os' in value
    );
}

export function isMobileServerSide(headers: IncomingHttpHeaders): boolean | undefined {
    try {
        const clientHintHeaders: Partial<Record<ClientHintHeaders, string>> = pick(
            Object.keys(headers)
                .filter((h) => headers[h])
                .reduce((acc, curr) => ({ ...acc, [curr]: headers[curr]?.toString() }), {}),
            clientHintsHeaders,
        );

        if (!isEmpty(clientHintHeaders['sec-ch-ua-mobile'])) {
            return clientHintHeaders['sec-ch-ua-mobile'] === '?1';
        }

        if (
            !isEmpty(clientHintHeaders['sec-ch-viewport-width']) &&
            !isNil(clientHintHeaders['sec-ch-viewport-width']) &&
            !Number.isNaN(headers['sec-ch-viewport-width'])
        ) {
            return (
                Number.parseInt(clientHintHeaders['sec-ch-viewport-width'], 10) < BREAKPOINTS_PX.md
            );
        }

        const ua = UAParser(clientHintHeaders).withClientHints();

        if (isUAIResult(ua) && !isEmpty(ua.ua) && !isEmpty(ua.device)) {
            return ua.device?.type === 'mobile';
        }
    } catch (err) {
        // ignore
    }

    return undefined;
}
